// ---------------------------------------------------------------------------
// AgentForm — create / view / edit form for a single LLM agent.
//
//   • create  — full form + breadcrumb/header; PUTs a new agent, then
//                navigates to its detail route.
//   • view     — all fields disabled (read-only); no footer. The detail view's
//                title bar owns the Edit/Delete actions.
//   • edit     — editable except the immutable name; PUT-replaces the working
//                copy and calls `onEditSaved`. No footer — Save/Cancel live in
//                the detail view's title bar, wired via `onEditControlsChange`.
//
// The form models AgentLlmSpec only; variables are edited via the
// VariablesEditor child. Metadata is system-managed and not edited here.
// ---------------------------------------------------------------------------

import {
  Body1,
  Button,
  Field,
  Input,
  Spinner,
  Textarea,
  Title2,
} from '@fluentui/react-components';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router';

import { ExpandableTextarea } from '../../../components/ExpandableTextarea/index.js';
import { TemplateVarCallouts } from '../../../components/TemplateVarCallouts/index.js';
import { VariablesEditor } from '../../../components/VariablesEditor/index.js';
import { useAgentMutations } from '../../../hooks/useAgentMutations/useAgentMutations.js';
import { useWorkspaces } from '../../../hooks/useWorkspaces/useWorkspaces.js';
import {
  AGENT_DESCRIPTION_MAX_LENGTH,
  AGENT_DISPLAY_NAME_MAX_LENGTH,
  AGENT_NAME_PATTERN,
  type Agent,
} from '../../../types/agent.js';

import { agentFormReducer } from './AgentForm.reducer.js';
import type { AgentFormStringField } from './AgentForm.reducer.js';
import { useStyles } from './AgentForm.style.js';
import type { AgentFormMode, VariableRow } from './AgentForm.types.js';
import { VARIABLE_TYPE_OPTIONS } from './AgentForm.types.js';
import {
  buildUpsertBody,
  collectBareTemplateTokens,
  emptyFormState,
  replaceBareTokenWithVar,
  seedFormFromAgent,
} from './AgentForm.utils.js';
import type {
  BareTemplateToken,
  SpecTemplateField,
} from './AgentForm.utils.js';
import { validateAgentForm } from './AgentForm.validation.js';

/** Human labels for the spec fields a bare {{...}} token can appear in. */
const SPEC_FIELD_LABELS: Record<SpecTemplateField, string> = {
  modelName: 'Model name',
  systemPrompt: 'System prompt',
  temperature: 'Temperature',
  topP: 'Top P',
  maxTokens: 'Max tokens',
  contextWindow: 'Context window',
  includeConversationHistory: 'Include conversation history',
  userMessage: 'User message',
};

/**
 * Edit-mode Save state reported up to the detail view so the Save action can
 * live in the title bar next to Edit (mirrors the Evaluate tab's run controls).
 */
export interface AgentEditControls {
  canSave: boolean;
  saving: boolean;
  save: () => void;
}

export interface AgentFormProps {
  mode?: AgentFormMode;
  initialAgent?: Agent;
  onCreated?: (agent: Agent) => void;
  onCancel?: () => void;
  onEditSaved?: (agent: Agent) => void;
  onEditCancelled?: () => void;
  /** Reports edit Save state up while in edit mode (null when unmounted). */
  onEditControlsChange?: (controls: AgentEditControls | null) => void;
}

export interface AgentFormOutletContext {
  onCreated: (agent: Agent) => void;
}

export function AgentForm({
  mode = 'create',
  initialAgent,
  onCreated,
  onCancel,
  onEditSaved,
  onEditCancelled,
  onEditControlsChange,
}: AgentFormProps = {}) {
  const styles = useStyles();
  const navigate = useNavigate();
  const outletContext = useOutletContext<AgentFormOutletContext | null>();
  const handleCreated = onCreated ?? outletContext?.onCreated;
  const { workspaceName: workspaceNameParam } = useParams<{
    workspaceName: string;
  }>();
  const { activeWorkspace, isLoading: workspaceLoading } = useWorkspaces();

  const viewOnly = mode === 'view';
  const editing = mode === 'edit';

  const [state, dispatch] = useReducer(
    agentFormReducer,
    initialAgent,
    (agent) => (agent ? seedFormFromAgent(agent) : emptyFormState()),
  );

  const {
    createAgent,
    replaceAgent,
    isSaving,
    error: saveError,
  } = useAgentMutations({ workspaceName: activeWorkspace?.name ?? null });

  const { errors, isValid } = useMemo(
    () => validateAgentForm(state, mode),
    [state, mode],
  );

  // ── Field + row updaters ───────────────────────────────────────────────
  const setField = useCallback((key: AgentFormStringField, value: string) => {
    dispatch({ type: 'SET_FIELD', key, value });
  }, []);

  const updateVariable = useCallback(
    (id: string, patch: Partial<VariableRow>) => {
      dispatch({ type: 'UPDATE_VARIABLE', id, patch });
    },
    [],
  );
  const addVariable = useCallback(() => {
    dispatch({ type: 'ADD_VARIABLE' });
  }, []);
  const removeVariable = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_VARIABLE', id });
  }, []);

  // Declare a referenced-but-undeclared $var: insert a row with the exact name
  // pre-filled (no retyping → no name typos); the user still fills type/default.
  const declareVariable = useCallback((name: string) => {
    dispatch({ type: 'DECLARE_VARIABLE', name });
  }, []);

  // Rewrite a bare {{NAME}} token into a valid {{$var:NAME}} reference.
  const convertBareToken = useCallback(
    (token: BareTemplateToken) => {
      dispatch({
        type: 'SET_FIELD',
        key: token.field,
        value: replaceBareTokenWithVar(state[token.field], token),
      });
    },
    [state],
  );

  const bareTokens = useMemo(() => collectBareTemplateTokens(state), [state]);

  // ── Navigation helpers (create mode) ───────────────────────────────────
  const basePath =
    workspaceNameParam !== undefined
      ? `/workspaces/${encodeURIComponent(workspaceNameParam)}/agents`
      : null;

  const handleCancel = useCallback(() => {
    if (editing) {
      onEditCancelled?.();
      return;
    }
    onCancel?.();
    if (basePath !== null) void navigate(basePath);
  }, [editing, onEditCancelled, onCancel, basePath, navigate]);

  const handleCreate = useCallback(async () => {
    if (!isValid) return;
    const name = state.name.trim();
    try {
      const created = await createAgent(name, buildUpsertBody(state));
      handleCreated?.(created);
      if (basePath !== null) {
        void navigate(`${basePath}/${encodeURIComponent(name)}`);
      }
    } catch {
      // saveError is surfaced from the mutations hook.
    }
  }, [isValid, state, createAgent, handleCreated, basePath, navigate]);

  const handleEditSave = useCallback(async () => {
    if (!isValid || initialAgent === undefined) return;
    try {
      // PUT replaces the whole working copy, so carry the system-managed
      // metadata through unchanged (it is not editable in the form).
      const updated = await replaceAgent(
        initialAgent.name,
        buildUpsertBody(state, initialAgent.metadata),
      );
      onEditSaved?.(updated);
    } catch {
      // saveError is surfaced from the mutations hook.
    }
  }, [isValid, initialAgent, replaceAgent, state, onEditSaved]);

  const canSubmit = isValid && !isSaving;

  // Report Save state up while editing so the detail view's title bar can own
  // the Save action (placed alongside Edit). Mirrors AgentEvaluateControls.
  useEffect(() => {
    if (!editing) return;
    onEditControlsChange?.({
      canSave: canSubmit,
      saving: isSaving,
      save: () => void handleEditSave(),
    });
  }, [editing, onEditControlsChange, canSubmit, isSaving, handleEditSave]);
  useEffect(() => () => onEditControlsChange?.(null), [onEditControlsChange]);

  // Workspace gating mirrors the eval-template form — only blocks the
  // editable (create) flow; view/edit render against the provided agent.
  if (!viewOnly && !editing && workspaceLoading) {
    return (
      <div className={styles.root}>
        <Spinner label="Loading workspace..." />
      </div>
    );
  }
  if (!viewOnly && !editing && activeWorkspace === null) {
    return (
      <div className={styles.root}>
        <Body1>
          Pick a workspace from the top bar before creating an agent.
        </Body1>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.scroll}>
        {mode === 'create' && (
          <>
            {activeWorkspace !== null && basePath !== null && (
              <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <span>
                  {activeWorkspace.displayName ?? activeWorkspace.name}
                </span>
                <span>›</span>
                <a
                  href={basePath}
                  className={styles.breadcrumbLink}
                  onClick={(e) => {
                    e.preventDefault();
                    handleCancel();
                  }}
                >
                  Agents
                </a>
                <span>›</span>
                <span className={styles.breadcrumbCurrent}>New agent</span>
              </nav>
            )}
            <div className={styles.header}>
              <Title2>New agent</Title2>
              <Body1 className={styles.subtitle}>
                Define an LLM agent to evaluate within this workspace.
              </Body1>
            </div>
          </>
        )}

        <fieldset className={styles.fieldsetReset}>
          <div className={styles.cards}>
            {/* ── Identity ─────────────────────────────────────────────── */}
            <section className={styles.card} aria-labelledby="card-identity">
              <div id="card-identity" className={styles.cardLabel}>
                Identity
              </div>
              <div className={styles.cardBody}>
                <div className={styles.fieldRow}>
                  <Field
                    className={styles.fieldColumn}
                    label="Name"
                    required
                    hint="Lowercase identifier (e.g. meeting-assistant). Cannot be changed later."
                    validationState={
                      errors.name === undefined ? 'none' : 'error'
                    }
                    validationMessage={errors.name}
                  >
                    <Input
                      className={styles.fullWidthInput}
                      value={state.name}
                      onChange={(_e, data) => setField('name', data.value)}
                      placeholder="my-agent"
                      pattern={AGENT_NAME_PATTERN.source}
                      disabled={mode !== 'create'}
                    />
                  </Field>
                  <Field className={styles.fieldColumn} label="Display name">
                    <Input
                      className={styles.fullWidthInput}
                      value={state.displayName}
                      onChange={(_e, data) =>
                        setField('displayName', data.value)
                      }
                      placeholder="My agent"
                      maxLength={AGENT_DISPLAY_NAME_MAX_LENGTH}
                      disabled={viewOnly}
                    />
                  </Field>
                </div>
                <ExpandableTextarea
                  label="Description"
                  value={state.description}
                  onChange={(value) => setField('description', value)}
                  rows={2}
                  placeholder="Human-readable description of what this agent does"
                  maxLength={AGENT_DESCRIPTION_MAX_LENGTH}
                  disabled={viewOnly}
                />
              </div>
            </section>

            {/* ── Variables ────────────────────────────────────────────── */}
            <section className={styles.card} aria-labelledby="card-variables">
              <div id="card-variables" className={styles.cardLabel}>
                Variables
                <span className={styles.cardLabelHelp}>
                  Parameterise this agent — keys use UPPER_SNAKE_CASE.
                </span>
              </div>
              <div className={styles.cardBody}>
                {!viewOnly && (
                  <TemplateVarCallouts
                    undeclaredVars={errors.undeclaredVars}
                    bareTokens={bareTokens}
                    fieldLabels={SPEC_FIELD_LABELS}
                    onDeclare={declareVariable}
                    onConvert={convertBareToken}
                  />
                )}

                <VariablesEditor
                  variables={state.variables}
                  typeOptions={VARIABLE_TYPE_OPTIONS}
                  errors={errors.variables}
                  disabled={viewOnly}
                  emptyLabel="No variables defined."
                  onUpdate={updateVariable}
                  onAdd={addVariable}
                  onRemove={removeVariable}
                />
              </div>
            </section>

            {/* ── Model configuration ──────────────────────────────────── */}
            <section className={styles.card} aria-labelledby="card-model">
              <div id="card-model" className={styles.cardLabel}>
                Model configuration
                <span className={styles.cardLabelHelp}>
                  Creation-time settings, fixed across all invocations.
                </span>
              </div>
              <div className={styles.cardBody}>
                <Field
                  label="Model name"
                  required
                  validationState={
                    errors.modelName === undefined ? 'none' : 'error'
                  }
                  validationMessage={errors.modelName}
                >
                  <Input
                    className={styles.fullWidthInput}
                    value={state.modelName}
                    onChange={(_e, data) => setField('modelName', data.value)}
                    placeholder="gpt-4o"
                    disabled={viewOnly}
                  />
                </Field>
                <ExpandableTextarea
                  label="System prompt"
                  required
                  value={state.systemPrompt}
                  onChange={(v) => setField('systemPrompt', v)}
                  rows={4}
                  disabled={viewOnly}
                  placeholder="You are a helpful assistant…"
                  validationState={
                    errors.systemPrompt === undefined ? 'none' : 'error'
                  }
                  validationMessage={errors.systemPrompt}
                />
                <div className={styles.fieldRow}>
                  <Field
                    className={styles.fieldColumn}
                    label="Temperature"
                    hint="0.0–2.0"
                  >
                    <Input
                      className={styles.fullWidthInput}
                      value={state.temperature}
                      onChange={(_e, data) =>
                        setField('temperature', data.value)
                      }
                      placeholder="default"
                      disabled={viewOnly}
                    />
                  </Field>
                  <Field
                    className={styles.fieldColumn}
                    label="Top P"
                    hint="0.0–1.0"
                  >
                    <Input
                      className={styles.fullWidthInput}
                      value={state.topP}
                      onChange={(_e, data) => setField('topP', data.value)}
                      placeholder="default"
                      disabled={viewOnly}
                    />
                  </Field>
                  <Field className={styles.fieldColumn} label="Max tokens">
                    <Input
                      className={styles.fullWidthInput}
                      value={state.maxTokens}
                      onChange={(_e, data) => setField('maxTokens', data.value)}
                      placeholder="default"
                      disabled={viewOnly}
                    />
                  </Field>
                </div>
                <div className={styles.fieldRow}>
                  <Field
                    className={styles.fieldColumn}
                    label="Context window"
                    hint="Previous turns of history to include. 0 = single-turn."
                  >
                    <Input
                      className={styles.fullWidthInput}
                      value={state.contextWindow}
                      onChange={(_e, data) =>
                        setField('contextWindow', data.value)
                      }
                      placeholder="0"
                      disabled={viewOnly}
                    />
                  </Field>
                  <Field
                    className={styles.fieldColumn}
                    label="Include conversation history"
                    hint="true / false"
                  >
                    <Input
                      className={styles.fullWidthInput}
                      value={state.includeConversationHistory}
                      onChange={(_e, data) =>
                        setField('includeConversationHistory', data.value)
                      }
                      placeholder="default"
                      disabled={viewOnly}
                    />
                  </Field>
                </div>
              </div>
            </section>

            {/* ── Invocation ───────────────────────────────────────────── */}
            <section className={styles.card} aria-labelledby="card-invoke">
              <div id="card-invoke" className={styles.cardLabel}>
                Invocation
                <span className={styles.cardLabelHelp}>
                  Per-execution settings, resolved fresh for each trial.
                </span>
              </div>
              <div className={styles.cardBody}>
                <Field
                  label="User message"
                  required
                  hint="Template applied per trial. May reference query/data via template expressions."
                  validationState={
                    errors.userMessage === undefined ? 'none' : 'error'
                  }
                  validationMessage={errors.userMessage}
                >
                  <Textarea
                    className={styles.fullWidthTextarea}
                    value={state.userMessage}
                    onChange={(_e, data) => setField('userMessage', data.value)}
                    rows={3}
                    placeholder="{{query}}"
                    disabled={viewOnly}
                  />
                </Field>
              </div>
            </section>

            {!viewOnly && saveError !== null && (
              <div className={styles.errorText} role="alert">
                {saveError}
              </div>
            )}
          </div>
        </fieldset>

        {mode === 'create' && (
          <div className={styles.footer}>
            <Button
              appearance="primary"
              disabled={!canSubmit}
              onClick={() => void handleCreate()}
            >
              {isSaving ? 'Creating…' : 'Create agent'}
            </Button>
            <Button appearance="secondary" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
